import {Maybe} from '@coolcolduk/typescript-util';
import {Home} from '@mui/icons-material';
import {Autocomplete, Box, IconButton, MenuItem, Paper, Select, TextField, Typography} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';

export interface FloatingSearchOption {
  title: string;
  tags?: string[];
  status?: string;
}

// Define the props interface
export interface FloatingSearchProps<T extends FloatingSearchOption> {
  /** available options in textbox search */
  mainOptions: T[];
  /** the option selected in the textbox dropdown */
  handleOptionSelect: (option: Maybe<string>) => void;
  /** placeholder text for the search input */
  searchPlaceholder?: string;
  /** handler for home button */
  handleHomeButton?: () => void;
}

export default function FloatingSearch<T extends FloatingSearchOption>({
  mainOptions,
  handleOptionSelect,
  handleHomeButton,
  searchPlaceholder = 'Search functionality',
}: FloatingSearchProps<T>) {
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const categories = useMemo(() => {
    const allCategories = mainOptions.map((option) => option.tags || []).flat();
    const allStatus = mainOptions.map((option) => option.status || '').filter(Boolean);
    return Array.from(new Set([...allCategories, ...allStatus]));
  }, [mainOptions]);

  useEffect(() => {
    setSearchValue('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  // Filter options based on search input and selected category
  const filteredOptions = useMemo(() => {
    const tagFiltered =
      selectedCategory === 'all'
        ? mainOptions
        : mainOptions.filter(
            (option) => (option.tags && option.tags.includes(selectedCategory)) || option.status === selectedCategory,
          );
    const titleOnly = tagFiltered.map((option) => option.title);
    return titleOnly.filter((option) =>
      searchValue.length ? option.toLowerCase().includes(searchValue.toLowerCase()) : true,
    );
  }, [mainOptions, searchValue, selectedCategory]);

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        top: '30px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '90%',
        maxWidth: '1200px',
        zIndex: 1000,
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {handleHomeButton && (
        <IconButton
          onClick={handleHomeButton}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Home />
        </IconButton>
      )}
      <Autocomplete
        freeSolo
        options={filteredOptions}
        sx={{flex: 1}}
        renderInput={(params) => <TextField {...params} placeholder={searchPlaceholder} variant="outlined" fullWidth />}
        inputValue={searchValue}
        onInputChange={(_e, newValue) => {
          setSearchValue(newValue);
        }}
        onChange={(_e, v) => handleOptionSelect(v)}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option}>
            <Typography variant="body1">{option}</Typography>
          </Box>
        )}
      />

      <Select
        value={selectedCategory}
        onChange={(event) => {
          setSelectedCategory(event.target.value);
        }}
      >
        <MenuItem value="all">All Categories</MenuItem>
        {categories.map((category) => (
          <MenuItem key={category} value={category}>
            {category}
          </MenuItem>
        ))}
      </Select>
    </Paper>
  );
}
